import { Injectable } from '@angular/core';
import { PublisherTagsAndPromotionalTypesSelectors } from 'app/modules/communication-centre/advertiser/store/publisher-tags-and-promotional-types/publisher-tags-and-promotional-types.selectors';
import { SelectableDataCategoryGroup } from 'app/modules/main/components/data-selector/data-selector.models';
import { SelectableDataCategory } from 'app/modules/main/components/data-selector/data-selector.models';
import { SelectableData } from 'app/modules/main/components/data-selector/data-selector.models';
import { SelectableDataCategoryFilter } from 'app/modules/main/components/data-selector/data-selector.models';
import { PublisherAccount } from 'app/modules/main/models/account';
import { isPromotionalType } from 'app/modules/main/models/publisher-tags-and-promotional-types';
import { isTag } from 'app/modules/main/models/publisher-tags-and-promotional-types';
import { NamedPublisherClass } from 'app/modules/main/models/publisher-tags-and-promotional-types';
import { PromotionalTypeGroup } from 'app/modules/main/models/publisher-tags-and-promotional-types';
import { PublisherTag } from 'app/modules/main/models/publisher-tags-and-promotional-types';
import { combineLatest } from 'rxjs';
import { of } from 'rxjs';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class PublisherClassFilter implements SelectableDataCategoryFilter<PublisherAccount, NamedPublisherClass> {

  private readonly tagsGroupLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.group.tags:Tags`;
  private readonly displayGroupLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.group.display:Display`;
  private readonly unsegmentedGroupLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.group.unsegmented:Unsegmented`;
  private readonly searchGroupLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.group.search:Search`;
  private readonly contentGroupLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.group.content:Content`;
  private readonly emailGroupLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.group.email:Email`;

  private readonly adNetworksCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.ad-networks:Ad Networks`;
  private readonly mediaBrokersCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.media-brokers:Media Brokers`;
  private readonly subNetworksCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.sub-networks:Sub Networks`;
  private readonly directTrafficCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.direct-traffic:Direct Traffic`;
  private readonly socialTrafficCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.social-traffic:Social Traffic`;
  private readonly mobileTrafficCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.mobile-traffic:Mobile Traffic`;
  private readonly retargetingDisplayCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.retargeting-display:Retargeting (Display)`;
  private readonly contextualTargetingCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.contextual-targeting:Contextual Targeting`;

  private readonly directLinkingCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.direct-linking:Direct Linking`;
  private readonly linkingViaLandingPagesCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.linking-via-landing-pages:Linking via Landing Pages`;
  private readonly socialSearchCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.social-search:Social Search`;
  private readonly mobileSearchCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.mobile-search:Mobile Search`;
  private readonly domainParkingCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.domain-parking:Domain Parking`;

  private readonly comparisonEngineCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.comparison-engine:Comparison Engine`;
  private readonly shoppingDirectoryCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.shopping-directory:Shopping Directory`;
  private readonly editorialContentCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.editorial-content:Editorial Content`;
  private readonly socialContentCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.social-content:Content Creators & Influencers`;
  private readonly mediaContentCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.media-content:Media Content`;
  private readonly communitiesUgcCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.communities-ugc:Communities & UGC`;
  private readonly cashbackCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.cashback:Cashback`;
  private readonly loyaltyCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.loyalty:Loyalty`;
  private readonly discountCodeCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.discount-code:Discount Code`;
  private readonly leadGenerationContentCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.lead-generation-content:Lead Generation (Content)`;
  private readonly virtualIncentivesCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.virtual-incentives:Virtual Incentives`;
  private readonly comparisonShoppingServiceLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.comparison-shopping-service:Comparison Shopping Service (CSS)`;

  private readonly newslettersCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.newsletters:Newsletters`;
  private readonly leadGenerationEmailCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.lead-generation-email:Lead Generation (Email)`;
  private readonly retargetingEmailCategoryLabel = $localize`:@@communication-centre.advertiser.publisher-selector.filter.category.retargeting-email:Retargeting (Email)`;

  private selectedCategory: SelectableDataCategory<NamedPublisherClass>;

  private readonly publisherTags: Observable<PublisherTag[]> =
    this.publisherTagsAndPromotionalTypesSelectors.getPublisherTagsAndPromotionalTypes()
      .pipe(map(publisherTagsAndPromotionalTypes => publisherTagsAndPromotionalTypes.publisherTags));

  private readonly promotionalTypes: Observable<PromotionalTypeGroup[]> =
    this.publisherTagsAndPromotionalTypesSelectors.getPublisherTagsAndPromotionalTypes()
      .pipe(map(publisherTagsAndPromotionalTypes => publisherTagsAndPromotionalTypes.promotionalTypes));

  readonly groups: Observable<SelectableDataCategoryGroup<NamedPublisherClass>[]> =
    combineLatest([this.publisherTags, this.promotionalTypes])
      .pipe(map(([publisherTags, promotionalTypes]) => this.toGroups(publisherTags, promotionalTypes)));

  constructor(
    private readonly publisherTagsAndPromotionalTypesSelectors: PublisherTagsAndPromotionalTypesSelectors,
  ) {
  }

  clear(): Observable<void> {

    this.selectedCategory = undefined;

    return of(null);
  }

  select(
    selectedCategory: SelectableDataCategory<NamedPublisherClass>
  ): Observable<void> {

    this.selectedCategory = selectedCategory;

    return of(null);
  }

  isMatch(
    selectablePublisher: SelectableData<PublisherAccount>
  ): boolean {

    if (this.selectedCategory === undefined) {
      return true;
    }

    const publisher = selectablePublisher.data;
    const selectedPublisherClass = this.selectedCategory.value;

    return (isTag(selectedPublisherClass) && publisher.tagIds.includes(selectedPublisherClass.id))
      || (isPromotionalType(selectedPublisherClass) && publisher.promotionalTypeIds.includes(selectedPublisherClass.id));
  }

  private toGroups(
    publisherTags: PublisherTag[],
    promotionalTypeGroups: PromotionalTypeGroup[]
  ): SelectableDataCategoryGroup<NamedPublisherClass>[] {

    const groups = [];

    if (!!publisherTags.length) {

      groups.push({
        label: this.tagsGroupLabel,
        categories: publisherTags.map(this.toCategory),
      });
    }

    if (!!promotionalTypeGroups.length) {

      groups.push(
        ...promotionalTypeGroups
          .filter(promotionalTypeGroup => !!promotionalTypeGroup.types.length)
          .map(promotionalTypeGroup => {
            return {
              label: this.localizeGroupLabel(promotionalTypeGroup.name),
              categories: promotionalTypeGroup.types.map(group => this.toLocalizedCategory(group)),
            };
          })
      );
    }

    return groups;
  }

  private toCategory(
    publisherClass: NamedPublisherClass
  ): SelectableDataCategory<NamedPublisherClass> {

    return {
      id: (publisherClass.type + '_' + String(publisherClass.id)),
      label: publisherClass.name,
      value: publisherClass,
    };
  }

  private toLocalizedCategory(
    publisherClass: NamedPublisherClass
  ): SelectableDataCategory<NamedPublisherClass> {

    return {
      id: (publisherClass.type + '_' + String(publisherClass.id)),
      label: this.localizeCategoryLabel(publisherClass.name),
      value: publisherClass,
    };
  }

  private localizeGroupLabel(
    label: string
  ): string {

    switch (label) {

      case 'Display':
        return this.displayGroupLabel;

      case 'Unsegmented':
        return this.unsegmentedGroupLabel;

      case 'Search':
        return this.searchGroupLabel;

      case 'Content':
        return this.contentGroupLabel;

      case 'Email':
        return this.emailGroupLabel;

      default:
        return label;

    }
  }

  private localizeCategoryLabel(
    label: string
  ): string {

    switch (label) {

      case 'Ad Networks':
        return this.adNetworksCategoryLabel;

      case 'Media Brokers':
        return this.mediaBrokersCategoryLabel;

      case 'Sub Networks':
        return this.subNetworksCategoryLabel;

      case 'Direct Traffic':
        return this.directTrafficCategoryLabel;

      case 'Social Traffic':
        return this.socialTrafficCategoryLabel;

      case 'Mobile Traffic':
        return this.mobileTrafficCategoryLabel;

      case 'Retargeting (Display)':
        return this.retargetingDisplayCategoryLabel;

      case 'Contextual Targeting':
        return this.contextualTargetingCategoryLabel;

      case 'Direct Linking':
        return this.directLinkingCategoryLabel;

      case 'Linking via Landing Pages':
        return this.linkingViaLandingPagesCategoryLabel;

      case 'Social Search':
        return this.socialSearchCategoryLabel;

      case 'Mobile Search':
        return this.mobileSearchCategoryLabel;

      case 'Domain Parking':
        return this.domainParkingCategoryLabel;

      case 'Comparison Engine':
        return this.comparisonEngineCategoryLabel;

      case 'Shopping Directory':
        return this.shoppingDirectoryCategoryLabel;

      case 'Editorial Content':
        return this.editorialContentCategoryLabel;

      case 'Social Content':
        return this.socialContentCategoryLabel;

      case 'Media Content':
        return this.mediaContentCategoryLabel;

      case 'Communities & UGC':
        return this.communitiesUgcCategoryLabel;

      case 'Cashback':
        return this.cashbackCategoryLabel;

      case 'Loyalty':
        return this.loyaltyCategoryLabel;

      case 'Discount Code':
        return this.discountCodeCategoryLabel;

      case 'Lead Generation (Content)':
        return this.leadGenerationContentCategoryLabel;

      case 'Virtual Incentives':
        return this.virtualIncentivesCategoryLabel;

      case 'Newsletters':
        return this.newslettersCategoryLabel;

      case 'Lead Generation (Email)':
        return this.leadGenerationEmailCategoryLabel;

      case 'Retargeting (Email)':
        return this.retargetingEmailCategoryLabel;

      case 'Comparison Shopping Service (CSS)':
        return this.comparisonShoppingServiceLabel;

      default:
        return label;

    }
  }

}
